// Copyright Epic Games, Inc. All Rights Reserved.
import { useRouter } from 'next/router';

import { UserGatingProvider } from '@contexts/UserGating';
import { ProtectedContentProvider } from '@contexts/ProtectedContent';
import { create403RedirectUrl, checkIsAccessDenied } from '@utils/user-gating';
import { APP_BASE_PATH } from '@constants/app';
import type { LocalesUnion } from '@constants/i18n';

import { useProtectedContentQuery } from './useProtectedContentQuery';

interface UserPermissionsGatingWrapperProps {
  platform: string;
  slug: string[];
  locale: LocalesUnion;
  children: React.ReactElement;
}

// The server-side endpoint reads protectedGroups directly from the document; the
// wrapper no longer needs to pass the perms list.
export const UserPermissionsGatingWrapper: React.FC<UserPermissionsGatingWrapperProps> = ({
  children,
  platform,
  slug,
  locale,
}) => {
  const { asPath, push } = useRouter();
  const { data, error } = useProtectedContentQuery(slug, locale);

  if (error) {
    if (checkIsAccessDenied(error.response.status)) {
      push(create403RedirectUrl(platform, `/${APP_BASE_PATH}`, asPath));
    } else {
      push('/500');
    }
  }

  return (
    <UserGatingProvider userGatingStatus={{ isLoading: !data && !error, isError: !!error }}>
      <ProtectedContentProvider value={data ?? null}>{children}</ProtectedContentProvider>
    </UserGatingProvider>
  );
};
